import { useEffect, useState } from "react";
import { toast } from "sonner";
import { axiosInstance } from "../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Link2, Copy, ExternalLink, RotateCcw, Tv, QrCode, MessageCircle, Plus } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { openWhatsApp } from "../utils/whatsappLink";

const shareText = (url, st) => `🎰 *${st.brand_name}* - Resultados de Sorteos en vivo\n\nRevisá aquí los números ganadores de Costa Rica, Nicaragua, Honduras, El Salvador, Panamá, República Dominicana y Anguilla, actualizados al momento:\n${url}${st.whatsapp ? `\n\n📲 WhatsApp ${st.whatsapp}` : ""}${st.sinpe ? `\n💸 Sinpe ${st.sinpe}` : ""}`;
import QRCode from "qrcode";
import { printHtmlInWindow } from "../utils/printHelper";
import { PublicPageSettings } from "./PublicPageSettings";
import { DEFAULT_PUBLIC_SETTINGS } from "../utils/brandTheme";
import { TvPromoManager } from "./TvPromoManager";

const FONT = "font-family:Helvetica,Arial,sans-serif;";
const letterHtml = (qr, st) => `<!DOCTYPE html><html><head><meta charset="utf-8"><title>QR Resultados</title>
<style>@page{size:letter;margin:0.6in}body{${FONT}margin:0;text-align:center;color:#0f172a}
h1{font-size:54px;margin:10px 0 4px;letter-spacing:-1px}h2{font-size:28px;margin:0 0 24px;font-weight:600;color:#334155}
img{width:5.2in;height:5.2in}.cta{font-size:30px;font-weight:700;margin:20px 0 28px}
.row{display:flex;justify-content:center;gap:28px;font-size:26px;font-weight:700}.pill{border:3px solid #0f172a;border-radius:40px;padding:10px 26px}</style></head>
<body><h1>${st.brand_name}</h1><h2>Resultados de Sorteos en vivo</h2><img src="${qr}" alt="QR"/>
<p class="cta">Escanea para ver los resultados</p>
<div class="row">${st.whatsapp ? `<span class="pill">WhatsApp ${st.whatsapp}</span>` : ""}${st.sinpe ? `<span class="pill">Sinpe ${st.sinpe}</span>` : ""}</div></body></html>`;
const labelHtml = (qr, st) => `<!DOCTYPE html><html><head><meta charset="utf-8"><title>QR Etiqueta</title>
<style>@page{size:2.5in 3in;margin:0.1in}body{${FONT}margin:0;text-align:center;color:#000}
p{margin:2px 0}.t{font-size:16px;font-weight:800}.s{font-size:10px;font-weight:600}img{width:2.1in;height:2.1in}</style></head>
<body><p class="t">${st.brand_name}</p><img src="${qr}" alt="QR"/><p class="s">Escanea: Resultados de Sorteos</p></body></html>`;

export const PublicResultsLink = ({ isSuperMaster }) => {
  const [links, setLinks] = useState([]);
  const [linkId, setLinkId] = useState(null);
  const [st, setSt] = useState(DEFAULT_PUBLIC_SETTINGS);
  const loadLinks = () => axiosInstance.get("/lottery/public-links").then((r) => {
    setLinks(r.data);
    setLinkId((cur) => cur || r.data[0]?.id || null);
  }).catch(() => {});
  useEffect(() => { loadLinks(); }, []);
  const link = links.find((l) => l.id === linkId);
  const token = link?.token;
  useEffect(() => {
    if (!token) return;
    axiosInstance.get(`/lottery/public-settings?link_id=${linkId}`).then((r) => setSt(r.data)).catch(() => {});
  }, [linkId, token]);
  if (!links.length) return null;

  const setToken = (t) => setLinks((ls) => ls.map((l) => (l.id === linkId ? { ...l, token: t } : l)));
  const create = async () => {
    const r = await axiosInstance.post(`/lottery/public-links/${linkId}`);
    setToken(r.data.token);
    toast.success(`Link creado para ${link.label}`);
  };
  const url = token ? `${window.location.origin}/resultados/${token}` : "";
  const printQr = async (format) => {
    const qr = await QRCode.toDataURL(url, { width: 900, margin: 1, errorCorrectionLevel: "M" });
    const html = format === "label" ? labelHtml(qr, st) : letterHtml(qr, st);
    if (!printHtmlInWindow(html, { width: 900, height: 1000 })) toast.error("Permite las ventanas emergentes para imprimir");
  };
  const copy = async () => {
    await navigator.clipboard.writeText(url);
    toast.success("Link copiado");
  };
  const regenerate = async () => {
    if (!window.confirm(`¿Generar un link nuevo para "${link.label}"? El link anterior dejará de funcionar.`)) return;
    const r = await axiosInstance.post(`/lottery/public-link/regenerate?link_id=${linkId}`);
    setToken(r.data.token);
    toast.success("Link nuevo generado");
  };
  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 flex flex-col gap-2" data-testid="public-results-link">
      <div className="flex flex-col md:flex-row md:items-center gap-2">
        <span className="flex items-center gap-1.5 text-sm font-semibold text-amber-900 shrink-0"><Link2 className="w-4 h-4" /> Link público</span>
        <Select value={linkId || undefined} onValueChange={setLinkId}>
          <SelectTrigger className="h-9 bg-white md:w-56 shrink-0" data-testid="public-link-selector"><SelectValue /></SelectTrigger>
          <SelectContent>
            {links.map((l) => (
              <SelectItem key={l.id} value={l.id} data-testid={`public-link-option-${l.id}`}>
                {l.id === "main" ? "General" : `Caja: ${l.label}`}{l.token ? "" : " (sin link)"}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {token
          ? <Input readOnly value={url} className="h-9 bg-white text-xs font-mono" onFocus={(e) => e.target.select()} data-testid="public-results-url" />
          : <Button size="sm" className="h-9 gap-1" onClick={create} data-testid="public-link-create"><Plus className="w-4 h-4" /> Crear link para {link?.label}</Button>}
      </div>
      {token && (
        <div className="flex flex-wrap gap-2">
          <Button size="sm" className="h-9 gap-1 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => openWhatsApp("", shareText(url, st))} data-testid="public-results-whatsapp"><MessageCircle className="w-4 h-4" /> WhatsApp</Button>
          <Button size="sm" variant="outline" className="h-9 gap-1" onClick={copy} data-testid="public-results-copy"><Copy className="w-4 h-4" /> Copiar</Button>
          <Button size="sm" variant="outline" className="h-9 gap-1" onClick={() => window.open(url, "_blank")} data-testid="public-results-open"><ExternalLink className="w-4 h-4" /> Abrir</Button>
          <Button size="sm" variant="outline" className="h-9 gap-1" onClick={() => window.open(`${url}?tv=1`, "_blank")} data-testid="public-results-tv"><Tv className="w-4 h-4" /> Modo TV</Button>
          <Button size="sm" variant="outline" className="h-9 gap-1" onClick={() => printQr("letter")} data-testid="public-results-qr-letter"><QrCode className="w-4 h-4" /> QR hoja carta</Button>
          <Button size="sm" variant="outline" className="h-9 gap-1" onClick={() => printQr("label")} data-testid="public-results-qr-label"><QrCode className="w-4 h-4" /> QR etiqueta</Button>
          {isSuperMaster && <TvPromoManager />}
          <PublicPageSettings key={linkId} linkId={linkId} settings={st} onSaved={setSt} />
          <Button size="sm" variant="outline" className="h-9 gap-1 text-red-600" onClick={regenerate} data-testid="public-results-regenerate"><RotateCcw className="w-4 h-4" /> Cambiar link</Button>
        </div>
      )}
    </div>
  );
};
